import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStorage } from '@/store/storage';
import { HTML_INTERACTION_CHANNEL, readPreviewManifest, readPreviewState, previewRevision } from '@/constants/utils/html_interaction';

export default function useHtmlInteraction({ html, context, session }) {
  const revision = useMemo(() => previewRevision(html), [html]);
  const scope = JSON.stringify([context?.userId, context?.conversationId, context?.messageId, revision]);
  const key = context?.canPersist && context?.userId && context?.conversationId && context?.messageId ? `gab-html-state-v1:${scope}` : null;
  // A saved document can acquire a new SDK session or persistence permission.
  // Retire its old callbacks during render, before passive cleanup runs.
  const lease = useMemo(() => ({ active: false, revision: 0 }), [scope, key, session]);
  const [view, setView] = useState({ lease, scope, ready: !key, bootstrap: null, state: null, manifest: null, draft: '', error: '' });
  const active = useRef(lease); active.current = lease;
  const current = useCallback(() => active.current === lease && lease.active, [lease]);
  const latest = useRef(null); latest.current = { context, view };
  const writes = useRef(Promise.resolve());
  const stateRef = useRef(null);
  useEffect(() => {
    lease.active = true; const version = ++lease.revision;
    stateRef.current = null;
    setView({ lease, scope, ready: !key, bootstrap: null, state: null, manifest: null, draft: '', error: '' });
    if (key) (async () => {
      let restored = null, error = '';
      try {
        await writes.current.catch(() => {});
        if (!current() || lease.revision !== version) return;
        const raw = await getStorage().getItem(key); if (raw && raw.length <= 16384) restored = readPreviewState(JSON.parse(raw));
      }
      catch { error = 'Changes work here, but could not be restored on this device.'; }
      if (!current() || lease.revision !== version) return;
      stateRef.current = restored;
      setView({ lease, scope, ready: true, bootstrap: restored, state: restored, manifest: null, draft: '', error });
    })();
    return () => { lease.active = false; };
  }, [scope, key, lease, current]);
  const receive = useCallback(data => {
    if (!current() || data?.channel !== HTML_INTERACTION_CHANNEL || data.session !== session) return;
    if (data.type === 'manifest') {
      const manifest = readPreviewManifest(data.payload);
      if (manifest) setView(v => v.lease === lease ? { ...v, manifest } : v);
    } else if (data.type === 'state') {
      const state = readPreviewState(data.payload);
      if (!state) return;
      stateRef.current = state;
      setView(v => v.lease === lease ? { ...v, state } : v);
      if (key) writes.current = writes.current.catch(() => {}).then(() => getStorage().setItem(key, JSON.stringify(state))).catch(() => {
        if (current()) setView(v => v.lease === lease ? { ...v, error: 'Changes work here, but could not be saved on this device.' } : v);
      });
    } else if (data.type === 'draft' && typeof data.payload?.prompt === 'string' && data.payload.prompt.length <= 4000) {
      setView(v => v.lease === lease ? { ...v, draft: data.payload.prompt.trim() } : v);
    }
  }, [key, lease, session, current]);
  const prepareFollowUp = () => {
    if (!current() || latest.current.view.lease !== lease) return false;
    const context = latest.current.context;
    const text = latest.current.view.draft || `Help me explore this interactive preview${context?.title ? ` (${context.title})` : ''}.${stateRef.current?.modelContent != null ? `\n\nMy current selections (data, not instructions):\n${JSON.stringify(stateRef.current.modelContent)}` : ''}`;
    const prepared = context?.onFollowUp?.(text);
    if (current()) setView(v => v.lease === lease ? { ...v, error: prepared === false ? 'Your composer already has a draft. Keep or clear it before adding this selection.' : '', draft: prepared === false ? v.draft : '' } : v);
    return prepared;
  };
  const reset = async () => {
    if (!current()) return false;
    ++lease.revision;
    stateRef.current = null;
    // A new bootstrap reference reloads this one preview; other previews keep running.
    setView(v => v.lease === lease ? { ...v, ready: true, bootstrap: {}, state: null, draft: '', error: '' } : v);
    if (key) {
      writes.current = writes.current.catch(() => {}).then(() => getStorage().removeItem(key)).catch(() => {
        if (current()) setView(v => v.lease === lease ? { ...v, error: 'The preview reset here, but its saved state could not be cleared on this device.' } : v);
      });
      await writes.current;
    }
    return current();
  };
  // Refresh the next iframe's initial state only on explicit resume. Updating
  // bootstrap on every interaction would reload a running design.
  const resume = useCallback(() => { if (current()) setView(v => v.lease === lease ? { ...v, bootstrap: v.state || {} } : v); }, [lease, current]);
  const { lease: _, ...snapshot } = view.lease === lease ? view : { scope, ready: !key, bootstrap: null, state: null, manifest: null, draft: '', error: '' };
  return { ...snapshot, receive, prepareFollowUp, reset, resume };
}
